import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Rocket, Target, Heart, CheckCircle2, Globe, Sparkles } from 'lucide-react';

const AboutUsPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6 }
        }
    };

    const values = [
        {
            icon: Heart,
            title: "Client First",
            description: "We measure our success by the success of our clients. Your goals are our goals.",
            color: "bg-red-50 text-red-600"
        },
        {
            icon: Rocket,
            title: "Innovation",
            description: "We constantly push boundaries, exploring new technologies to solve complex problems.",
            color: "bg-blue-50 text-blue-600"
        },
        {
            icon: Target,
            title: "Precision",
            description: "Excellence in every line of code. We take pride in delivering robust and polished solutions.",
            color: "bg-green-50 text-green-600"
        },
        {
            icon: Award,
            title: "Integrity",
            description: "Honesty and transparency are the foundations of all our professional relationships.",
            color: "bg-purple-50 text-purple-600"
        }
    ];

    const milestones = [
        { year: "2020", title: "The Beginning", description: "Mini App Tech was founded with a vision to simplify digital complexity." },
        { year: "2022", title: "Global Expansion", description: "Successfully delivered our 50th project and expanded our team to 20+ experts." },
        { year: "2024", title: "Product Launch", description: "Launched our first major proprietary SaaS platform, CollectApp." },
        { year: "2026", title: "Innovation Era", description: "Pioneering decentralized solutions and AI-driven business automation." }
    ];

    return (
        <div className="pt-24 min-h-screen bg-white">
            {/* Hero Section */}
            <section className="relative py-24 bg-dark text-white overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 -left-1/4 w-1/2 h-full bg-primary/40 blur-[120px] rounded-full" />
                    <div className="absolute bottom-0 -right-1/4 w-1/2 h-full bg-secondary/40 blur-[120px] rounded-full" />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-bold tracking-widest uppercase mb-8"
                        >
                            <Sparkles size={16} className="text-primary" /> Who We Are
                        </motion.div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-5xl md:text-7xl font-bold mb-8"
                        >
                            Empowering Businesses <br /> Through <span className="text-primary">Technology</span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
                        >
                            At Mini App Tech, we don't just build software; we architect the digital future.
                            Our mission is to bridge the gap between complex ideas and seamless user experiences.
                        </motion.p>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <div>
                                <h2 className="text-4xl font-bold text-dark mb-6">Driven by <span className="text-primary">Purpose</span></h2>
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    Founded in 2020, Mini App Tech emerged from a simple observation: businesses were struggling
                                    with outdated systems that hindered growth. We set out to change that by providing
                                    custom software that is as powerful as it is intuitive.
                                </p>
                            </div>

                            <div className="p-8 rounded-3xl bg-light border border-gray-100">
                                <h3 className="text-xl font-bold text-dark mb-4 flex items-center gap-3">
                                    <Globe className="text-primary" /> Our Global Vision
                                </h3>
                                <p className="text-gray-600">
                                    To become the world's most trusted partner in digital transformation,
                                    known for our relentless pursuit of excellence and our ability to turn
                                    visionary ideas into impactful realities.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="aspect-square bg-gradient-to-br from-primary to-secondary rounded-3xl overflow-hidden shadow-2xl relative">
                                <img
                                    src="https://images.unsplash.com/photo-1522071823991-b9623e43f507?w=1200&q=80"
                                    alt="Our Purposeful Team"
                                    className="w-full h-full object-cover mix-blend-overlay opacity-60"
                                />
                                <div className="absolute inset-0 flex items-center justify-center p-12">
                                    <div className="bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-xl flex flex-col gap-6 w-full max-w-xs">
                                        <div className="flex items-center gap-4">
                                            <Award size={32} className="text-primary" />
                                            <div>
                                                <p className="text-2xl font-bold text-dark">150+</p>
                                                <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Projects Delivered</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 border-t border-gray-100 pt-6">
                                            <Users size={32} className="text-secondary" />
                                            <div>
                                                <p className="text-2xl font-bold text-dark">50+</p>
                                                <p className="text-sm text-gray-500 font-medium uppercase tracking-wider">Tech Experts</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-24 bg-light">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-dark mb-4">Our Core <span className="text-primary">Values</span></h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">The principles that guide every decision we make and every line of code we write.</p>
                    </div>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
                    >
                        {values.map((val, idx) => (
                            <motion.div
                                key={idx}
                                variants={itemVariants}
                                className="bg-white p-8 rounded-2xl border border-gray-100 shadow-soft hover:shadow-soft-lg transition-all text-center"
                            >
                                <div className={`inline-block p-4 rounded-xl mb-6 ${val.color}`}>
                                    <val.icon size={32} />
                                </div>
                                <h3 className="text-xl font-bold text-dark mb-3">{val.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{val.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* History Timeline */}
            <section className="py-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-dark mb-4">Our <span className="text-primary">Journey</span></h2>
                </div>

                <div className="space-y-12 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                    {milestones.map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group"
                        >
                            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-primary text-white shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                                <CheckCircle2 size={16} />
                            </div>
                            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-6 rounded-2xl border border-gray-100 bg-white shadow-soft">
                                <div className="flex items-center justify-between space-x-2 mb-1">
                                    <div className="font-bold text-primary">{m.year}</div>
                                </div>
                                <div className="font-bold text-dark mb-1">{m.title}</div>
                                <div className="text-gray-500 text-sm">{m.description}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default AboutUsPage;
